import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { queryPipelineRunSummary, queryPipelines } from '@/features/pipelines/data';
import {
  buildCountedDropdownOptions,
  buildStringArrayCountMap,
  buildStringCountMap,
} from '@/features/shared/components/tableSystem/filterOptionCounts';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { queryKeys } from '@/shared/hooks';
import {
  buildContainsFilter,
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { buildBuildPipelineFilters, buildPipelineEntityFilters } from './pipelinePageFilters';
import { toPipelineRunDisplayStatusFilters } from './pipelineStatusScope';
import type { PipelineStatusKey } from '@/features/quality/components/qualityStatusDataHelpers';
import type { TableDropdownOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { TagOption } from '@/shared/data/tags';
import type { BuildStatus, PipelineRunSummaryItemDto } from '@flowqa/shared';

const PIPELINE_STATUS_SCOPE_PAGE_SIZE = 100;

export const fetchWindowedStatusPipelineIds = async ({
  effectiveProjectId,
  selectedOrganisationId,
  debouncedSearchValue,
  selectedPipelineIds,
  selectedStatuses,
  selectedTags,
  startDay,
  endDay,
}: {
  effectiveProjectId: string | null;
  selectedOrganisationId: string | null;
  debouncedSearchValue: string;
  selectedPipelineIds: string[];
  selectedStatuses: PipelineStatusKey[];
  selectedTags: string[];
  startDay: string | null;
  endDay: string | null;
}): Promise<string[]> => {
  const pipelineIds = new Set<string>();
  let page = 1;

  for (;;) {
    const result = await queryPipelineRunSummary(
      effectiveProjectId,
      selectedOrganisationId ?? undefined,
      {
        page,
        pageSize: PIPELINE_STATUS_SCOPE_PAGE_SIZE,
        sorts: [{ field: 'startedAt', direction: 'desc' }],
        filters: mergeFilters(
          buildContainsFilter('pipelineName', debouncedSearchValue),
          buildBuildPipelineFilters(selectedPipelineIds),
          buildInFilter('displayStatus', toPipelineRunDisplayStatusFilters(selectedStatuses)),
          buildInFilter('tags', selectedTags),
        ),
        metricsStartDay: startDay ?? undefined,
        metricsEndDay: endDay ?? undefined,
        latestOnly: false,
      },
    );

    result.runSummaries.forEach((runSummary: PipelineRunSummaryItemDto) => {
      pipelineIds.add(runSummary.pipelineId);
    });

    if (result.runSummaries.length < PIPELINE_STATUS_SCOPE_PAGE_SIZE) {
      break;
    }

    page += 1;
  }

  return Array.from(pipelineIds).sort((left, right) => left.localeCompare(right));
};

type UsePipelinesPageFilterOptionCountsArgs = {
  debouncedSearchValue: string;
  effectiveProjectId: string | null;
  isAuthenticated: boolean;
  pipelineOptions: readonly TableDropdownOption[];
  rangeEndIso: string | null;
  rangeStartIso: string | null;
  selectedBuildStatuses: BuildStatus[];
  selectedEndDay: string | null;
  selectedOrganisationId: string | null;
  selectedPipelineIds: string[];
  selectedStartDay: string | null;
  selectedStatuses: PipelineStatusKey[];
  selectedTags: string[];
  tagOptions: readonly TagOption[];
  useWindowedStatusFiltering: boolean;
};

type CountedPipelineFilterOptions = {
  countedPipelineOptions: TableDropdownOption[];
  countedTagOptions: Array<TagOption & { count: number }>;
};

export const usePipelinesPageFilterOptionCounts = ({
  debouncedSearchValue,
  effectiveProjectId,
  isAuthenticated,
  pipelineOptions,
  rangeEndIso,
  rangeStartIso,
  selectedBuildStatuses,
  selectedEndDay,
  selectedOrganisationId,
  selectedPipelineIds,
  selectedStartDay,
  selectedStatuses,
  selectedTags,
  tagOptions,
  useWindowedStatusFiltering,
}: UsePipelinesPageFilterOptionCountsArgs): CountedPipelineFilterOptions => {
  const startDay = selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? null;
  const endDay = selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? null;
  const pipelineCountStatusScopedPipelineIdsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'pipelines-status-scope',
      'pipeline-counts',
      selectedOrganisationId,
      effectiveProjectId,
      debouncedSearchValue,
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      startDay,
      endDay,
    ),
    enabled: isAuthenticated && useWindowedStatusFiltering,
    queryFn: async () =>
      await fetchWindowedStatusPipelineIds({
        effectiveProjectId,
        selectedOrganisationId,
        debouncedSearchValue,
        selectedPipelineIds: [],
        selectedStatuses,
        selectedTags,
        startDay,
        endDay,
      }),
  });
  const tagCountStatusScopedPipelineIdsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'pipelines-status-scope',
      'tag-counts',
      selectedOrganisationId,
      effectiveProjectId,
      debouncedSearchValue,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      startDay,
      endDay,
    ),
    enabled: isAuthenticated && useWindowedStatusFiltering,
    queryFn: async () =>
      await fetchWindowedStatusPipelineIds({
        effectiveProjectId,
        selectedOrganisationId,
        debouncedSearchValue,
        selectedPipelineIds,
        selectedStatuses,
        selectedTags: [],
        startDay,
        endDay,
      }),
  });
  const pipelineCountStatusScopedPipelineIds =
    useWindowedStatusFiltering && !pipelineCountStatusScopedPipelineIdsQuery.isError
      ? (pipelineCountStatusScopedPipelineIdsQuery.data ?? [])
      : null;
  const tagCountStatusScopedPipelineIds =
    useWindowedStatusFiltering && !tagCountStatusScopedPipelineIdsQuery.isError
      ? (tagCountStatusScopedPipelineIdsQuery.data ?? [])
      : null;
  const pipelineCountRowsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'pipelines-daily-history',
      'pipeline-counts',
      JSON.stringify({
        projectId: effectiveProjectId,
        organisationId: selectedOrganisationId,
        search: debouncedSearchValue,
        selectedBuildStatuses,
        selectedTags,
        startDay,
        endDay,
        useWindowedStatusFiltering,
        pipelineCountStatusScopedPipelineIds,
      }),
    ),
    enabled:
      isAuthenticated &&
      (!useWindowedStatusFiltering ||
        pipelineCountStatusScopedPipelineIds !== null ||
        pipelineCountStatusScopedPipelineIdsQuery.isError),
    queryFn: async () => {
      if (
        pipelineCountStatusScopedPipelineIds !== null &&
        pipelineCountStatusScopedPipelineIds.length === 0
      ) {
        return [];
      }

      const result = await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelines(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'name', direction: 'asc' }],
            filters: mergeFilters(
              buildContainsFilter('name', debouncedSearchValue),
              pipelineCountStatusScopedPipelineIds === null
                ? selectedStatuses.length > 0
                  ? buildInFilterWithNull(
                      'latestRunStatus',
                      selectedStatuses.includes('none')
                        ? [null, ...selectedBuildStatuses]
                        : selectedBuildStatuses,
                    )
                  : []
                : buildPipelineEntityFilters(pipelineCountStatusScopedPipelineIds),
              buildInFilter('tags', selectedTags),
            ),
            metricsStartDay: startDay ?? undefined,
            metricsEndDay: endDay ?? undefined,
            include: { latestRun: true, runSummaries: true, dailyHistory: true },
          }),
      );

      return result.items;
    },
  });
  const tagCountRowsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'pipelines-daily-history',
      'tag-counts',
      JSON.stringify({
        projectId: effectiveProjectId,
        organisationId: selectedOrganisationId,
        search: debouncedSearchValue,
        selectedBuildStatuses,
        selectedPipelineIds,
        startDay,
        endDay,
        useWindowedStatusFiltering,
        tagCountStatusScopedPipelineIds,
      }),
    ),
    enabled:
      isAuthenticated &&
      (!useWindowedStatusFiltering ||
        tagCountStatusScopedPipelineIds !== null ||
        tagCountStatusScopedPipelineIdsQuery.isError),
    queryFn: async () => {
      if (
        tagCountStatusScopedPipelineIds !== null &&
        tagCountStatusScopedPipelineIds.length === 0
      ) {
        return [];
      }

      const result = await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelines(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'name', direction: 'asc' }],
            filters: mergeFilters(
              buildContainsFilter('name', debouncedSearchValue),
              tagCountStatusScopedPipelineIds === null
                ? selectedStatuses.length > 0
                  ? buildInFilterWithNull(
                      'latestRunStatus',
                      selectedStatuses.includes('none')
                        ? [null, ...selectedBuildStatuses]
                        : selectedBuildStatuses,
                    )
                  : []
                : buildPipelineEntityFilters(tagCountStatusScopedPipelineIds),
              buildPipelineEntityFilters(selectedPipelineIds),
            ),
            metricsStartDay: startDay ?? undefined,
            metricsEndDay: endDay ?? undefined,
            include: { latestRun: true, runSummaries: true, dailyHistory: true },
          }),
      );

      return result.items;
    },
  });

  return useMemo(
    () => ({
      countedPipelineOptions: buildCountedDropdownOptions(
        pipelineOptions,
        buildStringCountMap(pipelineCountRowsQuery.data ?? [], (pipeline) => pipeline.id),
      ),
      countedTagOptions: buildCountedDropdownOptions(
        tagOptions,
        buildStringArrayCountMap(tagCountRowsQuery.data ?? [], (pipeline) => pipeline.tags),
      ),
    }),
    [pipelineCountRowsQuery.data, pipelineOptions, tagCountRowsQuery.data, tagOptions],
  );
};
